// CampaignLanding.jsx
// Public-facing landing for Golden Ticket campaigns. Treat this as a peculiar,
// memorable EVENT FLYER — not a pancake keepsake, not a gift. Strangers scan
// these in the wild; the pancake (if attached) is a visual hook, never the
// subject of the page. Event/stream/kickstarter/whatever comes first.
//
// Render states:
//   - loading : fetching campaign (+ optional attached order)
//   - expired : token.expiresAt is past, or campaign archived
//   - flyer   : hero + headline + body + CTAs + artist context + footer

import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { trackCampaignScan } from "../utils/campaigns";

const B = {
  navy:   "#1d52a0",
  blue:   "#1f59db",
  gold:   "#fcb31f",
  sky:    "#4aa4e1",
  pink:   "#ef537e",
  cream:  "#ffdaad",
  dark:   "#0d1833",
};

export default function CampaignLanding({ token }) {
  const [campaign, setCampaign]   = useState(null);
  const [order, setOrder]         = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Fetch campaign + optional linked order. Fire-and-forget scan tracking.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token?.campaignId) { setFetchError("This ticket isn't linked to a campaign."); return; }
      try {
        const snap = await getDoc(doc(db, "campaigns", token.campaignId));
        if (cancelled) return;
        if (!snap.exists()) { setFetchError("Campaign not found."); return; }
        const c = { id: snap.id, ...snap.data() };
        setCampaign(c);
        // If a pancake is attached, fetch it for the hero image.
        if (c.linkedOrderId) {
          try {
            const orderSnap = await getDoc(doc(db, "orders", c.linkedOrderId));
            if (!cancelled && orderSnap.exists()) setOrder({ id: orderSnap.id, ...orderSnap.data() });
          } catch { /* non-blocking */ }
        }
      } catch (e) {
        if (!cancelled) setFetchError(e.message);
      }
    })();
    trackCampaignScan(token?.id, token?.campaignId);
    return () => { cancelled = true; };
  }, [token?.id, token?.campaignId]);

  const expired = !!(token?.expiresAt?.toDate?.() && token.expiresAt.toDate() < new Date());

  if (expired) return <ExpiredScreen />;
  if (fetchError) return <ErrorScreen message={fetchError} />;
  if (!campaign) return <LoadingScreen />;
  if (campaign.archived) return <ExpiredScreen />;

  // Priority: campaign.heroImageUrl (custom) > attached pancake cutout > none.
  const cutoutUrl = order?.photo_cutout_url;
  const heroMode  = campaign.heroImageUrl ? "image"
                  : cutoutUrl             ? "pancake"
                  : null;

  return (
    <div style={{ background: B.navy, minHeight: "100vh", color: "white", fontFamily: "var(--jop-display)", maxWidth: "480px", margin: "0 auto", boxSizing: "border-box" }}>

      {/* Logo */}
      <div style={{ textAlign: "center", padding: "1rem 0 0.5rem" }}>
        <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "64px" }} />
      </div>

      {/* Golden Ticket banner with serial */}
      <div style={{
        background: "linear-gradient(135deg, rgba(252,179,31,0.18) 0%, rgba(255,218,173,0.08) 100%)",
        borderTop: `2px solid ${B.gold}`,
        borderBottom: `2px solid ${B.gold}`,
        padding: "1rem 1.5rem",
        textAlign: "center",
      }}>
        <div style={{ color: B.gold, fontWeight: 900, fontSize: "1.15rem", letterSpacing: "0.05em", textShadow: "0 2px 12px rgba(252,179,31,0.25)" }}>
          ✦ You Found a Golden Ticket ✦
        </div>
        {token?.snapshotSerialPrefix && (
          <div style={{ color: B.cream, fontSize: "0.7rem", opacity: 0.65, marginTop: "0.25rem", fontFamily: "monospace", letterSpacing: "0.08em" }}>
            {token.snapshotSerialPrefix}
            {token.catalogNumber ? `-${String(token.catalogNumber).padStart(4, "0")}` : ""}
          </div>
        )}
      </div>

      {/* Hero */}
      {heroMode === "image" && (
        <div style={{ width: "100%", aspectRatio: "16/9", background: "#000", overflow: "hidden" }}>
          <img src={campaign.heroImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      )}
      {heroMode === "pancake" && (
        <div style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1.25rem 1rem 0.5rem",
          background: "radial-gradient(ellipse at center, rgba(252,179,31,0.08) 0%, transparent 60%)",
        }}>
          <img
            src={cutoutUrl}
            alt=""
            style={{
              maxWidth: "80%",
              maxHeight: "280px",
              objectFit: "contain",
              filter: "drop-shadow(0 18px 36px rgba(0,0,0,0.55)) drop-shadow(0 4px 10px rgba(0,0,0,0.35))",
            }}
          />
        </div>
      )}

      {/* Headline + body — the flyer's main message */}
      <div style={{ padding: "1.25rem 1.5rem 0.5rem", textAlign: "center" }}>
        {campaign.headline && (
          <h1 style={{ color: "white", fontSize: "1.7rem", fontWeight: 900, margin: "0 0 0.6rem", lineHeight: 1.2 }}>
            {campaign.headline}
          </h1>
        )}
        {campaign.body && (
          <p style={{ color: B.cream, fontSize: "0.98rem", lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>
            {campaign.body}
          </p>
        )}
      </div>

      {/* CTAs */}
      {campaign.buttons?.length > 0 && (
        <div style={{ padding: "1.25rem 1.5rem 0.5rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {campaign.buttons.map((btn, i) => {
            if (!btn.label?.trim() || !btn.url?.trim()) return null;
            const primary = i === 0;
            return (
              <a
                key={i}
                href={btn.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  textAlign: "center",
                  background: primary ? B.gold : "rgba(255,255,255,0.08)",
                  color: primary ? B.dark : B.cream,
                  border: primary ? "none" : "1px solid rgba(255,218,173,0.35)",
                  borderRadius: "12px",
                  padding: "0.95rem",
                  fontSize: "1rem",
                  fontWeight: 800,
                  textDecoration: "none",
                }}
              >
                {btn.label}
              </a>
            );
          })}
        </div>
      )}

      {/* Artist context — short, not a biography. Gives strangers the why. */}
      <div style={{ padding: "1.5rem 1.5rem 0.75rem", borderTop: "1px solid rgba(255,255,255,0.1)", marginTop: "1.25rem" }}>
        <div style={{ color: B.cream, fontSize: "0.85rem", lineHeight: 1.6, opacity: 0.75, textAlign: "center", margin: "0 0 0.75rem" }}>
          The Joy of Pancakes is <strong>Doctor Dan the Pancake Man</strong> — live pancake portraits drawn with batter on a hot griddle.
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
          <a href="https://twitch.tv/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, maxWidth: "160px", textAlign: "center", background: "rgba(74,164,225,0.15)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", padding: "0.55rem", color: B.sky, fontWeight: 600, fontSize: "0.82rem", textDecoration: "none" }}>
            📺 Twitch
          </a>
          <a href="https://instagram.com/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, maxWidth: "160px", textAlign: "center", background: "rgba(239,83,126,0.12)", border: "1px solid rgba(239,83,126,0.3)", borderRadius: "10px", padding: "0.55rem", color: B.pink, fontWeight: 600, fontSize: "0.82rem", textDecoration: "none" }}>
            📸 Instagram
          </a>
        </div>
      </div>

      {/* Subtle "we do parties" — passive awareness, no push */}
      <div style={{ padding: "0.5rem 1.5rem 1rem", textAlign: "center" }}>
        <a href="https://thejoyofpancakes.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.75rem", textDecoration: "underline", textDecorationColor: "rgba(255,218,173,0.25)", textUnderlineOffset: "3px" }}>
          We also do private events & parties →
        </a>
      </div>

      {/* Tombstone */}
      <div style={{ padding: "0.75rem 1.5rem 2rem", textAlign: "center", color: "rgba(255,255,255,0.25)", fontSize: "0.68rem" }}>
        One of a limited series of Golden Tickets hidden in the wild.
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ background: B.navy, minHeight: "100vh", color: B.cream, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--jop-display)" }}>
      Loading your Golden Ticket…
    </div>
  );
}

function ExpiredScreen() {
  return (
    <div style={{ background: B.navy, minHeight: "100vh", color: "white", fontFamily: "var(--jop-display)", maxWidth: "480px", margin: "0 auto", padding: "2rem 1.5rem", textAlign: "center" }}>
      <div style={{ textAlign: "center", padding: "1rem 0 1rem" }}>
        <img src="/jop_logo_wide.png" alt="The Joy of Pancakes" style={{ height: "64px" }} />
      </div>
      <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>⌛</div>
      <h1 style={{ color: B.gold, fontSize: "1.4rem", fontWeight: 900, margin: "0 0 0.5rem" }}>This Golden Ticket has expired</h1>
      <p style={{ color: B.cream, fontSize: "0.95rem", lineHeight: 1.55, margin: "0 0 1.5rem", opacity: 0.85 }}>
        But there's always another one waiting. Come find us at the next event.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", maxWidth: "320px", margin: "0 auto" }}>
        <a href="https://thejoyofpancakes.com" target="_blank" rel="noopener noreferrer"
          style={{ display: "block", textAlign: "center", background: B.gold, color: B.dark, border: "none", borderRadius: "12px", padding: "0.85rem", fontSize: "1rem", fontWeight: 800, textDecoration: "none" }}>
          🥞 See where we'll be next
        </a>
        <a href="https://twitch.tv/thejoyofpancakes" target="_blank" rel="noopener noreferrer"
          style={{ display: "block", textAlign: "center", background: "rgba(74,164,225,0.15)", border: "1px solid rgba(74,164,225,0.3)", borderRadius: "10px", padding: "0.7rem", color: B.sky, fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" }}>
          📺 Watch Live on Twitch
        </a>
      </div>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={{ background: B.navy, minHeight: "100vh", color: "white", fontFamily: "var(--jop-display)", padding: "2rem 1.5rem", textAlign: "center" }}>
      <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>😕</div>
      <h1 style={{ color: B.gold, fontSize: "1.2rem", fontWeight: 800, margin: "0 0 0.5rem" }}>Something's off</h1>
      <p style={{ color: B.cream, fontSize: "0.9rem", opacity: 0.8 }}>{message}</p>
    </div>
  );
}
